import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Wrench, 
  HardHat, 
  Wifi, 
  Settings, 
  ClipboardList,
  ArrowRight 
} from 'lucide-react';
import engineeringImage from '@assets/generated_images/Engineering_team_reviewing_blueprints_542c2b6b.png';
import constructionImage from '@assets/generated_images/Drilling_rig_construction_equipment_9c780340.png';
import maintenanceImage from '@assets/generated_images/Bucket_truck_maintenance_work_d40115b8.png';
import fulfillmentImage from '@assets/generated_images/Fiber_fulfillment_technical_work_e2f5a19d.png';
import projectManagementImage from '@assets/generated_images/Project_manager_on_site_2b1d4d4c.png';

const services = [
  {
    icon: <Wrench className="h-8 w-8" />,
    title: "Engineering & Design",
    description: "Comprehensive fiber network design, surveying, and technical documentation for optimal infrastructure deployment.",
    image: engineeringImage,
    features: ["Network Design", "Site Surveys", "Technical Documentation", "Permitting Support"]
  },
  {
    icon: <HardHat className="h-8 w-8" />,
    title: "Construction Services", 
    description: "Complete fiber optic construction including aerial, underground, and wireless infrastructure installation.",
    image: constructionImage,
    features: ["Aerial Construction", "Underground Installation", "Directional Drilling", "Civil Works"]
  },
  {
    icon: <Wifi className="h-8 w-8" />,
    title: "Network Fulfillment",
    description: "End-to-end installation and activation services for residential and business fiber connections.",
    image: fulfillmentImage,
    features: ["Fiber Splicing", "Equipment Installation", "Testing & Activation", "Customer Premises"]
  },
  {
    icon: <Settings className="h-8 w-8" />,
    title: "Maintenance & Support",
    description: "24/7 network maintenance, emergency restoration, and ongoing support services for optimal performance.",
    image: maintenanceImage,
    features: ["Emergency Response", "Preventive Maintenance", "Network Monitoring", "Fault Resolution"]
  },
  {
    icon: <ClipboardList className="h-8 w-8" />,
    title: "Project Management",
    description: "Expert project coordination and management for large-scale fiber infrastructure deployments.",
    image: projectManagementImage,
    features: ["Program Management", "Resource Coordination", "Timeline Management", "Quality Assurance"]
  }
];

export default function ServicesSection() {
  const handleLearnMore = (serviceName) => {
    console.log(`Learn more about ${serviceName}`);
    const contactElement = document.getElementById('contact');
    if (contactElement) {
      // Get the element's position relative to the document
      const rect = contactElement.getBoundingClientRect();
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
      const elementTop = rect.top + currentScroll;
      const headerHeight = 100; // Fine-tuned offset for optimal positioning
      const targetPosition = elementTop - headerHeight;
      
      console.log('Element top:', elementTop);
      console.log('Target scroll position:', targetPosition);
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    } else {
      console.log('Contact element not found!');
    }
  };

  return (
    <section className="py-16 bg-background scroll-mt-20" id="services">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Comprehensive Infrastructure Services
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            From initial planning to ongoing maintenance, we provide end-to-end telecom infrastructure 
            solutions that connect communities and power digital transformation.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12 items-stretch">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className="group hover:shadow-lg transition-all duration-300 border hover:border-primary/20 flex flex-col h-full"
              data-testid={`service-card-${index}`}
            >
              <CardHeader className="pb-4">
                <div className="inline-flex items-center space-x-3 p-4 bg-primary/10 rounded-lg text-primary mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {service.icon}
                  <CardTitle className="text-xl font-semibold text-current">{service.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="aspect-video relative overflow-hidden rounded-lg bg-muted mb-4">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2 flex-1 mb-4">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-muted-foreground">
                      <ArrowRight className="h-3 w-3 text-primary mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  variant="ghost" 
                  className="w-full group-hover:bg-primary group-hover:text-primary-foreground"
                  onClick={() => handleLearnMore(service.title)}
                  data-testid={`button-learn-more-${index}`}
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="max-w-2xl mx-auto mb-8">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Start Your Project?
            </h3>
            <p className="text-muted-foreground">
              Contact our expert team today for a consultation and customized solution proposal 
              tailored to your specific infrastructure needs.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => {
                const contactElement = document.getElementById('contact');
                if (contactElement) {
                  contactElement.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              data-testid="button-get-quote"
            >
              Get Free Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}